import { useEffect, useMemo } from "react";
import Form from "../../Helpers/Form/Form";
import { Offcanvas } from "react-bootstrap";
import FormHeader from "../../Helpers/Form/FormParts/FormHeader";
import trans, { isRTL } from "../../../utils";
import EditCallCenterFormBody from "./EditCallCenterFormBody";
import {
  useGetServiceRequestForEditQuery,
  useUpdateServiceRequestMutation,
} from "../../../Redux/service/CallCenter/CallCenter";
import { Formik, Form as FormFormik } from "formik";
import Button from "../../Helpers/Button/Button";
import * as Yup from "yup";
import dayjs from "dayjs";
import { useContext } from "react";
import { popupcontext } from "../../../context/Popupscontext";
import { toast } from "react-toastify";
import Loader from "../../Helpers/Loader/Loader";
import {
  COMPLAINT_DESCRIPTION_MAX_LENGTH,
  MAX_SECONDARY_PHONES,
  isValidEgyptianPhone,
} from "./callCenterValidation";
import { getLocalizedColorLabel } from "./serviceRequestColors";

export default function EditCallCenterRequestForm({ editRequestId, setEditRequestId }) {
  const { setEdited } = useContext(popupcontext);

  const {
    data: editData,
    isFetching,
    refetch,
  } = useGetServiceRequestForEditQuery(editRequestId, {
    skip: !editRequestId,
    refetchOnMountOrArgChange: true,
  });

  const [updateServiceRequest] = useUpdateServiceRequestMutation();

  useEffect(() => {
    if (editRequestId) refetch();
  }, [editRequestId]);

  const data = editData?.data;
  const editableFields = data?.editableFields || {};
  const lang = localStorage.getItem("lang") === "ar" ? "ar" : "en";

  const initialValues = useMemo(
    () => ({
      landline: data?.customer?.landline || "",
      secondaryPhones: data?.customer?.secondaryPhones || [],
      state: data?.customer?.state || "",
      city: data?.customer?.city || "",
      villageId: data?.customer?.villageId || "",
      address: data?.customer?.address || "",
      latitude: data?.customer?.latitude ?? "",
      longitude: data?.customer?.longitude ?? "",
      productId: data?.productId || "",
      modelId: data?.modelId || "",
      color: getLocalizedColorLabel(data?.color, lang) || "",
      defects: (data?.defects || []).map((d) => d.id),
      sparePartIds: data?.sparePartIds || [],
      complaintDescription: data?.complaintDescription || "",
      preferredVisitTime: data?.preferredVisitTime
        ? dayjs(data.preferredVisitTime).format("YYYY-MM-DD HH:mm:ss")
        : "",
      preferredVisitTimeEnd: data?.preferredVisitTimeEnd
        ? dayjs(data.preferredVisitTimeEnd).format("YYYY-MM-DD HH:mm:ss")
        : "",
    }),
    [data],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        address: editableFields.address
          ? Yup.string().required(trans("Address is required"))
          : Yup.string(),
        productId: editableFields.productId
          ? Yup.string().required(trans("Product is required"))
          : Yup.string(),
        modelId: editableFields.modelId
          ? Yup.string().required(trans("Model is required"))
          : Yup.string(),
        defects: editableFields.defects
          ? Yup.array().min(1, trans("At least one defect is required"))
          : Yup.array(),
        complaintDescription: editableFields.complaintDescription
          ? Yup.string()
              .required(trans("Complaint description is required"))
              .min(10, trans("Complaint description cannot minimum 10 characters"))
              .max(
                COMPLAINT_DESCRIPTION_MAX_LENGTH,
                trans(`Complaint description cannot exceed ${COMPLAINT_DESCRIPTION_MAX_LENGTH} characters`),
              )
          : Yup.string(),
        secondaryPhones: Yup.array().of(
          Yup.string()
            .required(trans("Phone number is required"))
            .test(
              "valid-egyptian-phone",
              trans("Enter a valid Egyptian mobile or landline number"),
              isValidEgyptianPhone,
            ),
        ).max(
          MAX_SECONDARY_PHONES,
          trans(`You can add up to ${MAX_SECONDARY_PHONES} additional phone numbers`),
        ).test(
          "unique-phones",
          localStorage.getItem("lang") === "ar"
            ? "يجب أن تكون أرقام الهواتف الإضافية فريدة"
            : "Secondary phone numbers must be unique",
          function (phones) {
            if (!Array.isArray(phones)) return true;
            const normalizedPhones = phones
              .filter((p) => p && typeof p === "string")
              .map((p) => p.replace(/\D/g, ""));
            const uniquePhones = new Set(normalizedPhones);
            return uniquePhones.size === normalizedPhones.length;
          },
        ),
      }),
    [editableFields],
  );

  const handleClose = () => setEditRequestId(false);

  const handleFormSubmit = async (values, setSubmitting) => {
    setSubmitting(true);

    const payload = {
      expectedUpdatedAt: data?.updatedAt || undefined,
      address: editableFields.address ? values.address || "" : undefined,
      latitude: editableFields.address ? values.latitude || undefined : undefined,
      longitude: editableFields.address ? values.longitude || undefined : undefined,
      landline: editableFields.landline ? values.landline || "" : undefined,
      secondaryPhones: editableFields.secondaryPhones ? values.secondaryPhones || [] : undefined,
      state: editableFields.state ? values.state || undefined : undefined,
      city: editableFields.city ? values.city || undefined : undefined,
      villageId: editableFields.village ? values.villageId || null : undefined,
      productId: editableFields.productId ? values.productId : undefined,
      modelId: editableFields.modelId ? values.modelId || null : undefined,
      color: editableFields.color ? values.color || "" : undefined,
      defects: editableFields.defects ? values.defects || [] : undefined,
      sparePartIds: editableFields.defects ? values.sparePartIds || [] : undefined,
      complaintDescription: editableFields.complaintDescription ? values.complaintDescription : undefined,
      preferredVisitTime: editableFields.preferredVisitTime ? values.preferredVisitTime || "" : undefined,
      preferredVisitTimeEnd: editableFields.preferredVisitTimeEnd
        ? values.preferredVisitTimeEnd || ""
        : undefined,
    };

    try {
      await updateServiceRequest({ id: editRequestId, payload }).unwrap();
      toast.success(trans("Service Request Updated Successfully"), {
        autoClose: 1500,
        rtl: localStorage.getItem("lang") === "ar",
      });
      setEdited(true);
      handleClose();
    } catch (err) {
      if (err?.status === 409) {
        toast.error(trans(err?.data?.message ?? "This request has been modified by another user. Please reload the latest version before continuing."), {
          autoClose: 3000,
          rtl: localStorage.getItem("lang") === "ar",
        });
        refetch();
      } else {
        const errorMsg = trans(
          err?.data?.message ?? "Unable to update the Service Request. Please try again.",
        );
        toast.error(errorMsg, {
          autoClose: 2000,
          rtl: localStorage.getItem("lang") === "ar",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form show={!!editRequestId}>
      <Offcanvas.Header>
        <FormHeader title={trans("Edit Service Request")} onClose={handleClose} />
      </Offcanvas.Header>
      <Offcanvas.Body>
        {isFetching || !data ? (
          <div className="loader-container">
            <Loader />
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={(values, { setSubmitting }) => handleFormSubmit(values, setSubmitting)}
            validationSchema={validationSchema}
          >
            {({ handleSubmit, isSubmitting }) => (
              <FormFormik onSubmit={handleSubmit} dir={!isRTL ? "ltr" : "rtl"}>
                <EditCallCenterFormBody editData={data} />
                <div className="offcanvas-footer m-0">
                  <div className="offcanvas-footer-body gap-8">
                    <Button
                      type={"primary"}
                      htmlType="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting || !data.canEdit}
                    >
                      {isSubmitting ? trans("Saving...") : trans("Save")}
                    </Button>
                    <Button
                      type={"default"}
                      htmlType="button"
                      disabled={isSubmitting}
                      onClick={handleClose}
                    >
                      {trans("Cancel")}
                    </Button>
                  </div>
                </div>
              </FormFormik>
            )}
          </Formik>
        )}
      </Offcanvas.Body>
    </Form>
  );
}
